import { Dialog, Typography, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { type FC, useEffect, useState } from 'react';
import { getProDashboardTapsReportsData } from '../../models';
import { downloadCsvBlob } from '../../utils';
import { SuspenseLoader } from '../LoadingIndicators';

type AvaniExportProps = {
  startDate: DateTime;
  endDate: DateTime;
  visitId: string;
  onComplete: () => void;
};

export const AvaniExport: FC<AvaniExportProps> = ({ startDate, endDate, visitId, onComplete }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [isExportFinished, setIsExportFinished] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    setOpen(false);
    onComplete();
  };

  useEffect(() => {
    if (inProgress) return;
    const today = DateTime.now().setZone('utc', { keepLocalTime: true });
    const isStartDayAfterToday = startDate.startOf('day') > today.startOf('day');

    if (isStartDayAfterToday) {
      setOpen(true);
      setError('Please select a start date before or equal to today.');
      return;
    }

    if (startDate > endDate) {
      setOpen(true);
      setError('Start date should be before end date.');
      return;
    }

    setInProgress(true);
    (async () => {
      try {
        const { result } = await getProDashboardTapsReportsData(visitId, startDate.toISO(), endDate.toISO());

        downloadCsvBlob(result, `${startDate.toFormat('dd-MM')}_${endDate.toFormat('dd-MM')}.csv`);

        setIsExportFinished(true);
      } catch (e) {
        setError('Unknown error occured.');
        console.log(e);
      } finally {
        setInProgress(false);
        onComplete();
      }
    })();
    //todo:v2-changes
  }, []);

  return (
    <>
      {inProgress && <SuspenseLoader />}
      {!inProgress && (
        <Dialog open={open} onClose={handleClose}>
          <Typography
            variant={'body1'}
            sx={{
              color: theme.colors.avani.magenta,
              padding: 5
            }}
          >
            {isExportFinished ? 'File exported successfully.' : error || 'Unknown error occured.'}
          </Typography>
        </Dialog>
      )}
    </>
  );
};
